import {RouteNames} from '@/brief-hub/router/route-names';
import {NavigationGuard, RouteLocationNormalized} from 'vue-router';
import store from '@/brief-hub/store';
import {ActionsType} from '@/brief-hub/store/modules/brief/actions';
import {DraftState, DraftStateWrapper} from '@/common/api/api-raw-types';

export class NavigationService {
    public ff4JBriefFunnelUpdateCategorySuggestion = window.features?.ff4JBriefFunnelUpdateCategorySuggestion || false;

    private navigation = [
        RouteNames.BRIEF_FREELANCER_PROFILE,
        ...(this.ff4JBriefFunnelUpdateCategorySuggestion ? [] : [RouteNames.BRIEF_FREELANCER_JOB_TITLE]),
        RouteNames.BRIEF_FREELANCER_SKILLS,
        RouteNames.BRIEF_WHEN_CONDITIONS,
        RouteNames.BRIEF_WHERE_CONDITIONS,
        RouteNames.BRIEF_MISSION_DESCRIPTION,
        RouteNames.BRIEF_OVERVIEW,
    ];

    private nextRouteFrom: Partial<Record<DraftState, string>> = {
        INITIALIZED: RouteNames.BRIEF_FREELANCER_PROFILE,
        ...(this.ff4JBriefFunnelUpdateCategorySuggestion
            ? {FREELANCER_SECTION_COMPLETED: RouteNames.BRIEF_FREELANCER_SKILLS}
            : {FREELANCER_SECTION_COMPLETED: RouteNames.BRIEF_FREELANCER_JOB_TITLE}),
        FREELANCER_JOB_TITLE_SECTION_COMPLETED: RouteNames.BRIEF_FREELANCER_SKILLS,
        FREELANCER_SKILLS_SECTION_COMPLETED: RouteNames.BRIEF_WHEN_CONDITIONS,
        WHEN_CONDITIONS_SECTION_COMPLETED: RouteNames.BRIEF_WHERE_CONDITIONS,
        WHERE_CONDITIONS_SECTION_COMPLETED: RouteNames.BRIEF_MISSION_DESCRIPTION,
        MISSION_TITLE_SECTION_COMPLETED: RouteNames.BRIEF_MISSION_DESCRIPTION,
        MISSION_DESCRIPTION_SECTION_COMPLETED: RouteNames.BRIEF_OVERVIEW,
        NOT_DRAFT_ANYMORE: RouteNames.BRIEF_DETAIL,
    };

    requireDraftState(requiredDraftState: DraftState): NavigationGuard {
        return async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: (route?: Partial<RouteLocationNormalized>) => void) => {
            await this.getBriefIfNeeded(to.params.id as string);

            const brief = store.state.brief.brief;
            if (!brief || !brief.draftState) {
                next({name: RouteNames.NOT_FOUND});
            } else if (this.isRequiredDraftStateCompleted(brief.draftState as DraftStateWrapper, requiredDraftState)) {
                next();
            } else {
                const routeName = this.nextIncompleteRoute(brief.draftState as DraftStateWrapper);
                next({name: routeName, params: to.params, query: to.query});
            }
        };
    }

    private isRequiredDraftStateCompleted(draftStateWrapper: DraftStateWrapper, requiredDraftState: DraftState): boolean {
        const draftStateList = Object.keys(this.nextRouteFrom);

        const completed = draftStateList.indexOf(draftStateWrapper.draftState);
        const required = draftStateList.indexOf(requiredDraftState);

        return completed >= required;
    }

    nextIncompleteRoute(draftStateWrapper: DraftStateWrapper): string {
        if (draftStateWrapper === undefined) {
            return RouteNames.NOT_FOUND;
        }
        return this.nextRouteFrom[draftStateWrapper.draftState] || RouteNames.NOT_FOUND;
    }

    nextRoute(routeName: string): string {
        const currentRouteIndex = this.navigation.indexOf(routeName);
        if (currentRouteIndex < this.navigation.length) {
            return this.navigation[currentRouteIndex + 1];
        }
        return RouteNames.NOT_FOUND;
    }

    previousRoute(routeName: string): string | null {
        const currentRouteIndex = this.navigation.indexOf(routeName);
        if (currentRouteIndex > 0) {
            return this.navigation[currentRouteIndex - 1];
        }
        return null;
    }

    isRouteAfter(route1: string, route2: string): boolean {
        const route1Index = this.navigation.indexOf(route1);
        const route2Index = this.navigation.indexOf(route2);
        return route1Index > route2Index;
    }

    private async getBriefIfNeeded(briefId: string): Promise<void> {
        const brief = store.state.brief;
        if (!brief.brief || brief.brief.briefId !== briefId) {
            await store.dispatch(ActionsType.GET_BRIEF, briefId);
        }
    }
}
